import clamp from 'lodash/clamp';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import round from 'lodash/round';
import toFinite from 'lodash/toFinite';
import { RefObject } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useRef } from 'react';
import { useMemo } from 'react';
import { useCallback } from 'react';

export const NumberFormat = {
    DECIMAL: 'decimal',
    PERCENT: 'percent',
    CURRENCY: 'currency',
    UNIT: 'unit',
};

export interface NumberFormatProps {
    /**
     * Формат числа
     */
    format?: 'decimal' | 'percent' | 'currency' | 'unit';
    /**
     * Минимальное значение
     */
    min?: number;
    /**
     * Максимальное значение
     */
    max?: number;
    /**
     * Точность
     */
    precision?: number;
    /**
     * Фиксированная точность (количество знаков после запятой)
     */
    fixedPrecision?: boolean;
}

const setDefaultNumber = (value: any, defaultValue: number): number =>
    isNumber(value) ? value : defaultValue;

const setDefaultString = (value: any, defaultValue: string): string =>
    isString(value) ? value : defaultValue;

const createFormatters = ({
    min,
    max,
    precision,
    fixedPrecision,
}: NumberFormatProps) => {
    const needClamp = isNumber(min) || isNumber(max);
    const lower = setDefaultNumber(min, -Infinity);
    const upper = setDefaultNumber(max, Infinity);

    const intlFormat = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
        ...(isNumber(precision) && {
            ...(fixedPrecision && {
                minimumFractionDigits: precision,
            }),
            maximumFractionDigits: precision,
        }),
        useGrouping: false,
    });

    /**
     * Форматирование числа, приведение к границам min, max т округление до заданной точности
     */
    const formatNumber = (value: number | string | null) => {
        let formattedValue = value;
        if (isString(formattedValue)) {
            formattedValue = formattedValue
                .replace(/\s/g, '')
                .replace(/,/g, '.');
        }
        formattedValue = toFinite(formattedValue);
        if (needClamp) {
            formattedValue = clamp(formattedValue, lower, upper);
        }
        if (isNumber(precision)) {
            formattedValue = round(formattedValue, precision);
        }
        return formattedValue;
    };

    /**
     * Форматирование значения для возврата в onChange
     */
    const formatInputNumber = (value: string): number | null => {
        if (value === '') {
            return null;
        }
        return formatNumber(value);
    };

    /**
     * Форматирование значения для отображения в поле ввода
     */
    const formatValueNumber = (value: number | null): string => {
        if (value === null) {
            return '';
        }
        return intlFormat.format(formatNumber(value));
    };

    return { formatInputNumber, formatValueNumber };
};

type UseNumberFormatParams = NumberFormatProps & {
    value: any;
    onChange: (value: any) => void;
    onBlur?: (event: any) => void;
    onFocus?: (event: any) => void;
    inputRef?: RefObject<HTMLInputElement>;
};

export const useNumberFormat = ({
    value: originalValue,
    onChange: originalOnChange,
    onBlur: originalOnBlur,
    onFocus: originalOnFocus,
    format,
    precision: originalPrecision,
    fixedPrecision: originalFixedPrecision = false,
    min: originalMin,
    max: originalMax,
    inputRef: originalInputRef,
}: UseNumberFormatParams) => {
    let min = originalMin;
    let max = originalMax;
    let precision = originalPrecision;
    let fixedPrecision = originalFixedPrecision;

    const internalInputRef = useRef<HTMLInputElement>(null);
    const inputRef = originalInputRef || internalInputRef;

    switch (format) {
        case NumberFormat.DECIMAL:
            break;
        case NumberFormat.PERCENT:
            min = setDefaultNumber(min, 0);
            max = setDefaultNumber(max, 100);
            precision = setDefaultNumber(precision, 2);
            break;
        case NumberFormat.CURRENCY:
            precision = setDefaultNumber(precision, 2);
            fixedPrecision = true;
            break;
        case NumberFormat.UNIT:
            min = setDefaultNumber(min, 0);
            precision = setDefaultNumber(precision, 5);
            fixedPrecision = false;
            break;
        default:
    }

    const { formatInputNumber, formatValueNumber } = useMemo(
        () => createFormatters({ min, max, precision, fixedPrecision }),
        [min, max, precision, fixedPrecision]
    );

    const [isActive, setActive] = useState(false);
    const [inputValue, setInputValue] = useState(
        formatValueNumber(originalValue)
    );

    const handleChange = useCallback(
        (nextValue) => {
            const formattedValue = formatInputNumber(nextValue);
            originalOnChange?.(formattedValue);
        },
        [formatInputNumber, originalOnChange]
    );

    const handleFocus = useCallback(
        (event: any) => {
            setActive(true);
            originalOnFocus?.(event);
        },
        [originalOnFocus]
    );

    const handleBlur = useCallback(
        (event: any) => {
            setActive(false);
            if (inputRef.current) {
                inputRef.current.value = formatValueNumber(originalValue);
            }
            originalOnBlur?.(event);
        },
        [inputRef, originalOnBlur, formatValueNumber, originalValue]
    );

    useEffect(() => {
        if (!isActive) {
            setInputValue(formatValueNumber(originalValue));
        }
    }, [originalValue, isActive, formatValueNumber]);

    return {
        value: inputValue,
        inputRef,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        min,
        max,
    };
};
